import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { getQuestionById } from '../../data/assessment/questionBank';
import styles from './LessonContentRenderer.module.css';

interface InteractionRevealProps {
    label: string;
    content: string;
    type?: string;
    questionId?: string;
}

const cleanExplanation = (text: string): string => {
    if (!text) return '';
    // Remove "**Correct Action**: **" or "**Correct Action**:" prefix (case-insensitive)
    let cleaned = text.replace(/^\*\*Correct\s+Action\*\*:\s*\*\*?/i, '');
    
    // Remove trailing "**"
    if (cleaned.endsWith('**')) {
        cleaned = cleaned.slice(0, -2);
    }
    
    // Remove other patterns like "Correct Action:" or "Answer:" or "Scenario:"
    cleaned = cleaned.replace(/^(Correct\s+Action|Answer|Scenario):\s*/i, '');
    
    return cleaned.trim();
};

const cleanOption = (text: string): string => {
    if (!text) return '';
    return text.replace(/^\*\*+(.*?)\*\*+$/, '$1').trim();
};

const parseInlineMarkdownToJSX = (text: string): React.ReactNode => {
    if (!text) return '';
    const parts = text.split('**');
    return parts.map((part, index) => {
        if (index % 2 === 1) {
            return <strong key={index}>{part}</strong>;
        }
        const subParts = part.split('*');
        return subParts.map((subPart, subIndex) => {
            if (subIndex % 2 === 1) {
                return <em key={`${index}-${subIndex}`}>{subPart}</em>;
            }
            return subPart;
        });
    });
};

const InteractionReveal: React.FC<InteractionRevealProps> = ({ label, content, type = 'click-to-reveal', questionId }) => {
    const [isVisible, setIsVisible] = useState(false);
    const isExamReveal = type === 'exam-reveal' || type === 'exam-click-to-reveal';

    // Hydrate from question bank if questionId is provided
    const question = questionId ? getQuestionById(questionId) : null;
    
    let finalLabel = label;
    let finalContent = content;
    let questionText = '';
    let correctOption = '';
    let explanationText = '';
    
    if (question) {
        const categoryName = question.category || 'Exam Priority';
        finalLabel = `Safety Drill: ${categoryName} Awareness`;
        questionText = question.text;
        
        const rawCorrectOption = question.options && question.correctAnswer != null
            ? question.options[Number(question.correctAnswer)]
            : 'Unknown';
        correctOption = cleanOption(rawCorrectOption);
            
        const rawExplanation = question.explanation || question.scenario || 'Study this core concept carefully for the final state examination.';
        explanationText = cleanExplanation(rawExplanation);

        // Keep fallback finalContent for compatibility if needed
        finalContent = `Scenario: ${question.scenario || question.text}<br/><br/>**Correct Action**: **${rawCorrectOption}**.<br/><br/>**Explanation**: ${question.explanation || 'Study this core concept carefully for the final state examination.'}`;
    }

    return (
        <motion.div
            className={isExamReveal ? `${styles.interactionContainer} ${styles.interactionGold}` : styles.interactionContainer}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
        >
            {isExamReveal && question ? (
                <div className={styles.examRevealCardBody}>
                    <div className={styles.interactionGoldHeader}>
                        <div className={styles.interactionGoldBadge}>
                            <Trophy size={14} />
                            <span>Exam Priority Drill</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                            <span className={styles.interactionLabelGold}>{finalLabel}</span>
                            <div className={styles.interactionGoldLottie}>
                                <DotLottieReact
                                    src="/assets/animations/quiz-gold-trophy.lottie"
                                    autoplay
                                    loop
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.examQuestionText}>
                        {parseInlineMarkdownToJSX(questionText)}
                    </div>

                    <button
                        onClick={() => setIsVisible(!isVisible)}
                        className={styles.examRevealToggleButton}
                        aria-expanded={isVisible}
                    >
                        <span className={styles.interactionIconGold}>{isVisible ? '−' : '+'}</span>
                        <span>{isVisible ? 'Hide Practice Answer' : 'Click to Reveal Practice Answer'}</span>
                    </button>

                    <AnimatePresence>
                        {isVisible && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div className={`${styles.interactionContent} ${styles.interactionContentGold}`}>
                                    <div className={styles.correctActionBlock}>
                                        <span className={styles.correctActionLabel}>Correct Action:</span>
                                        <span className={styles.correctActionValue}>{correctOption}</span>
                                    </div>
                                    <div className={styles.explanationBlock}>
                                        <span className={styles.explanationLabel}>Explanation:</span>
                                        {parseInlineMarkdownToJSX(explanationText)}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            ) : (
                <>
                    <button
                        onClick={() => setIsVisible(!isVisible)}
                        className={isExamReveal ? `${styles.interactionButton} ${styles.interactionButtonGold}` : styles.interactionButton}
                        aria-expanded={isVisible}
                    >
                        {isExamReveal ? (
                            <div className={styles.interactionGoldHeader}>
                                <div className={styles.interactionGoldBadge}>
                                    <Trophy size={14} />
                                    <span>Exam Priority Drill</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
                                    <span className={styles.interactionIconGold}>{isVisible ? '−' : '+'}</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left', flex: 1 }}>
                                        <span className={styles.interactionLabelGold}>{finalLabel}</span>
                                        {!isVisible && (
                                            <span className={styles.interactionCTAGold}>
                                                Click to Reveal Practice Answer
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.interactionGoldLottie}>
                                        <DotLottieReact
                                            src="/assets/animations/quiz-gold-trophy.lottie"
                                            autoplay
                                            loop
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <span className={styles.interactionIcon}>{isVisible ? '−' : '+'}</span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left', flex: 1 }}>
                                    <span>{finalLabel}</span>
                                    {!isVisible && (
                                        <span className={styles.interactionCTA}>
                                            Click to Reveal Answer
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </button>

                    <AnimatePresence>
                        {isVisible && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div className={isExamReveal ? `${styles.interactionContent} ${styles.interactionContentGold}` : styles.interactionContent} dangerouslySetInnerHTML={{ __html: finalContent }} />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}
        </motion.div>
    );
};

export default InteractionReveal;
